import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Form, Formik, FormikValues } from 'formik';
import { ActivateAccountFormInput } from '../../interfaces/interfaces';
import { emailSchema } from '../../utils/formSchema';
import {
  SendEmailVerificationDocument,
  UserStatusInSystemDocument,
  VerifyRecaptchaDocument,
} from '../../queries';
import { logger } from '../../utils/logging';
import { useNavigate, useSearchParams } from 'react-router-dom';
import VerifyEmail from '../verify-email/verify-email';
import { getStatusRoute } from '../../utils/status-route';
import { withMergedParams } from '../../utils/url-utils';
import { useSetUserStatus } from '../../hooks/use-set-user-status';
import { RegisterLoading } from '../register-loading/register-loading';
import {
  EMAIL_LOOKUP_ERROR_MESSAGE,
  VERIFICATION_EMAIL_ERROR_MESSAGE,
} from '../../utils/constants';

export function Register() {
  const initialValues: ActivateAccountFormInput = {
    email: '',
  };

  const [getUserStatusInSystem] = useLazyQuery(UserStatusInSystemDocument, {
    fetchPolicy: 'no-cache',
  });
  const [getVerifyRecaptcha] = useLazyQuery(VerifyRecaptchaDocument, {
    fetchPolicy: 'no-cache',
  });
  const [sendEmailVerification] = useMutation(SendEmailVerificationDocument);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const setUserStatus = useSetUserStatus();

  const [error, setError] = useState<string>();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isRecaptchaValid = async (
    runRecaptcha: (action: string) => Promise<string>
  ): Promise<boolean> => {
    try {
      const captchaToken = await runRecaptcha('register');
      const { data, error } = await getVerifyRecaptcha({
        variables: { captchaToken },
      });
      return !error && data?.verifyRecaptcha === true;
    } catch (error) {
      logger.error('Failed to execute recaptcha', error);
      return false;
    }
  };

  const lookupUserStatus = async (email: string) => {
    const { data, error } = await getUserStatusInSystem({
      variables: { email },
    });
    logger.info(data);
    const status = data?.userStatusInSystem;
    return !error && status?.__typename === 'UserStatusItem'
      ? status
      : undefined;
  };

  const sendVerificationEmail = async (
    email: string,
    runRecaptcha: (action: string) => Promise<string>
  ): Promise<boolean> => {
    try {
      const captchaToken = await runRecaptcha('new_user_verify');
      const { data } = await sendEmailVerification({
        variables: { email, captchaToken },
      });
      return data?.sendEmailVerification === true;
    } catch (error) {
      logger.error('Failed to send verification email', error);
      return false;
    }
  };

  const handleNext = async (values: FormikValues) => {
    const { email } = values;
    setError(undefined);

    if (!executeRecaptcha || !(await isRecaptchaValid(executeRecaptcha))) {
      setError(EMAIL_LOOKUP_ERROR_MESSAGE);
      return;
    }

    const status = await lookupUserStatus(email);
    if (!status) {
      setError(EMAIL_LOOKUP_ERROR_MESSAGE);
      return;
    }

    const route = getStatusRoute(status.userStatus);
    if (route.type === 'redirect') {
      window.location.assign(route.url);
      return;
    }

    if (route.type === 'verify-email') {
      const isSent = await sendVerificationEmail(email, executeRecaptcha);
      if (!isSent) {
        setError(VERIFICATION_EMAIL_ERROR_MESSAGE);
        return;
      }
      navigate(withMergedParams(route.path, searchParams), {
        state: { from: 'new-user' },
      });
      return;
    }

    setUserStatus({ ...status, email });
    navigate(withMergedParams(route.path, searchParams));
  };

  return (
    <Formik
      validateOnChange={true}
      initialValues={initialValues}
      validationSchema={emailSchema}
      onSubmit={handleNext}
      validateOnMount={false}
    >
      {({ errors, touched, submitForm, isSubmitting }) =>
        isSubmitting ? (
          <RegisterLoading />
        ) : (
          <Form>
            <VerifyEmail
              handleSubmit={submitForm}
              isLoading={isSubmitting}
              error={error}
              errors={errors}
              touched={touched}
            />
          </Form>
        )
      }
    </Formik>
  );
}

export default Register;
